import { apiClient } from './client';
import type { Track } from '../types';

export interface CurrentTrackResponse {
  current: {
    trackId: string;
    track: Track;
    kind: 'music' | 'ad';
    startTime: string;
    endTime: string;
    duration: number;
    elapsed: number;
    remaining: number;
  };
  next: {
    trackId: string;
    track: Track;
    kind: 'music' | 'ad';
    startTime: string;
    duration: number;
    startsIn: number;
  } | null;
  serverTime: string;
}

export interface AvailableMoodsResponse {
  moods: string[];
}

export const radioApi = {
  /**
   * Get current playing track for a profile/mood
   */
  getCurrentTrack: async (
    profileSlug: string,
    mood: string
  ): Promise<CurrentTrackResponse> => {
    const response = await apiClient.get<CurrentTrackResponse>(
      `/radio/${profileSlug}/${mood}/current`
    );
    return response.data;
  },

  /**
   * Build the HLS stream URL for a profile/mood. The endpoint returns
   * an .m3u8 playlist that loops 20 tracks ahead — the player keeps
   * itself fed without JS-driven track switching, which is what makes
   * background / lock-screen playback survive on mobile.
   *
   * For authenticated users we append `?token=<jwt>` so the backend
   * can resolve audience as 'paid' (ad-free). The browser's <audio>
   * element and hls.js don't send Authorization headers by default,
   * and the backend's JwtStrategy already accepts ?token=… as a
   * fallback (it does the same thing for SSE). Without this, paid
   * users would silently fall back to the guest stream with ads.
   */
  getStreamUrl: (profileSlug: string, mood: string): string => {
    const base = `/api/v1/radio/${profileSlug}/${encodeURIComponent(mood)}/stream`;
    const token = localStorage.getItem('authToken');
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  },

  /**
   * Get available moods for a profile
   */
  getAvailableMoods: async (
    profileSlug: string
  ): Promise<AvailableMoodsResponse> => {
    const response = await apiClient.get<AvailableMoodsResponse>(
      `/radio/${profileSlug}/moods`
    );
    return response.data;
  },
};
